import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { partyManagerAPI } from "../api/partyManagerAPI";
import useUserStore from "../zustand/UseUserStore";
import { toast } from "sonner";

export const useInviteAcceptance = () => {
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const state = useUserStore();

	const [inviteData, setInviteData] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [error, setError] = useState(null);

	// Extract invite parameters from URL
	const getInviteParams = useCallback(() => {
		const params = {
			partyId: searchParams.get("partyId"),
			contestId: searchParams.get("contestId"),
			memberId: searchParams.get("memberId"),
			role: searchParams.get("role"),
			type: searchParams.get("type"),
			inviteType: searchParams.get("inviteType"),
			gameMasterID: searchParams.get("gameMasterID"),
			contest: searchParams.get("contest"),
			inviter: searchParams.get("inviter") || searchParams.get("invitedby"),
			party: searchParams.get("party") || searchParams.get("partyName"),
			partyIcon: searchParams.get("partyIcon"),
			partyIconType: searchParams.get("partyIconType") || "emoji",
			acceptingInvitation: searchParams.get("acceptinginvitation") === "true",
			// Additional parameters that might come from QRValidate/AcceptInvite
			partyid: searchParams.get("partyid"), // Alternative partyId parameter
			contestID: searchParams.get("contestID"), // Alternative contestId parameter
			memberid: searchParams.get("memberid"), // Alternative memberId parameter
			gamemasterID: searchParams.get("gamemasterID"), // Alternative gameMasterID parameter
			// Selected party information for gamemaster-recruited-member invites
			selectedPartyId: searchParams.get("selectedPartyId"),
			selectedPartyName: searchParams.get("selectedPartyName"),
			selectedPartyIcon: searchParams.get("selectedPartyIcon"),
			selectedPartyIconType: searchParams.get("selectedPartyIconType"),
			selectedPartyMembers: searchParams.get("selectedPartyMembers"),
			selectedPartyScore: searchParams.get("selectedPartyScore"),
		};

		// Check if we have the minimum required parameters for an invite
		// Use both primary and alternative parameter names
		const hasInviteParams =
			params.partyId ||
			params.partyid ||
			params.contestId ||
			params.contestID ||
			params.role ||
			params.inviteType ||
			params.gameMasterID ||
			params.gamemasterID;

		return hasInviteParams ? params : null;
	}, [searchParams]);

	// Process the invite acceptance
	const processInviteAcceptance = useCallback(
		async (inviteParams) => {
			if (!inviteParams || !state.user?.MemberID) {
				console.log("No invite params or user not authenticated");
				return;
			}

			setIsProcessing(true);
			setError(null);

			try {
				console.log("Processing invite acceptance with params:", inviteParams);

				// Use primary parameters, fall back to alternative names
				const partyId = inviteParams.partyId || inviteParams.partyid;
				const contestId = inviteParams.contestId || inviteParams.contestID;
				const memberId = inviteParams.memberId || inviteParams.memberid;
				const gameMasterID =
					inviteParams.gameMasterID || inviteParams.gamemasterID;
				const role = inviteParams.role;
				const type = inviteParams.type;
				const inviteType = inviteParams.inviteType;
				const party = inviteParams.party;
				const inviter = inviteParams.inviter;
				const partyIcon = inviteParams.partyIcon;
				const partyIconType = inviteParams.partyIconType;
				const contest = inviteParams.contest;

				// Selected party information for gamemaster-recruited-member invites
				const selectedPartyId = inviteParams.selectedPartyId;
				const selectedPartyName = inviteParams.selectedPartyName;
				const selectedPartyIcon = inviteParams.selectedPartyIcon;
				const selectedPartyIconType = inviteParams.selectedPartyIconType;
				const selectedPartyMembers = inviteParams.selectedPartyMembers;
				const selectedPartyScore = inviteParams.selectedPartyScore;

				// Determine the invite type and role
				const currentInviteType = inviteType || type || "ringleader-recruited";
				const inviteRole = role || "member";

				// Determine party ID based on invite type
				let partyIdToUse;
				if (
					currentInviteType === "gamemaster-recruited-member" ||
					currentInviteType === "gamemaster-recruited-ringleader" ||
					currentInviteType === "gamemaster-recruited-judge"
				) {
					// For gamemaster-recruited invites, use selected party ID if available
					if (
						currentInviteType === "gamemaster-recruited-member" &&
						selectedPartyId
					) {
						partyIdToUse = selectedPartyId;
					} else {
						// For other gamemaster invites, use 0 for new parties or the provided partyId
						partyIdToUse = partyId || 0;
					}
				} else if (currentInviteType === "member-recruited") {
					// For member-recruited invites, use the partyId from the URL
					partyIdToUse = partyId;
				} else {
					partyIdToUse = partyId || 0;
				}

				// Call the accept invite API
				const response = await partyManagerAPI.acceptInvite(
					partyIdToUse,
					parseInt(contestId),
					state.user.MemberID,
					inviteRole,
					currentInviteType,
					party || "Party",
					state.user.Username || "Member",
					false // override
				);

				console.log("Accept invite API response:", response);

				if (
					response.success ||
					response.message === "Invitation accepted successfully"
				) {
					// Update user roles if provided in response
					if (response.result?.user_roles) {
						state.setUserWithRoles(state.user, response.result.user_roles);
					}

					// For gamemaster-recruited-ringleader invites, show the Ringleader Invitation Accepted page
					if (currentInviteType === "gamemaster-recruited-ringleader") {
						console.log(
							"Gamemaster-recruited-ringleader invite accepted - showing Ringleader Invitation Accepted page"
						);

						// Set up the ringleader success data for the AcceptInvite page
						const ringleaderData = {
							isRingleader: true,
							partyName: response.result?.party_name || party || "Your Party",
							partyIcon: response.result?.party_icon || partyIcon || null,
							partyIconType:
								response.result?.party_icon_type || partyIconType || "emoji",
							partyVibes: response.result?.party_vibes || "",
							partyMessage: response.result?.party_message || "",
							contestID: contestId,
							inviteType: currentInviteType,
							contestName: contest || "Contest",
							inviter: inviter || "Unknown",
						};

						// Store the ringleader data in localStorage for the AcceptInvite page to pick up
						localStorage.setItem(
							"ringleaderInviteAccepted",
							JSON.stringify(ringleaderData)
						);

						// Redirect to AcceptInvite page which will show the success page
						const acceptInviteURL = `/accept-invite/${partyIdToUse}/${contestId}/${state.user.MemberID}/${inviteRole}/${currentInviteType}`;
						window.location.href = acceptInviteURL;
						return;
					}

					// For all other invite types, show a toast notification
					const displayPartyName =
						currentInviteType === "gamemaster-recruited-member" &&
						selectedPartyName
							? selectedPartyName
							: party || "Party";
					const displayContestName = contest;
					const roleName =
						inviteRole.charAt(0).toUpperCase() + inviteRole.slice(1);

					// Create the confirmation message
					let message = `🎉 Congratulations! You've `;

					if (displayPartyName && displayPartyName !== "Party") {
						message += `joined the ${displayPartyName} party`;
						if (displayContestName) {
							message += ` for the ${displayContestName} contest`;
						}
					} else if (displayContestName) {
						message += `been added to the ${displayContestName} contest`;
					} else {
						message += `joined the party`;
					}

					message += ` as a ${roleName}!`;

					console.log("📦 Showing toast notification:", message);
					toast.success(message, { duration: 5000 });

					console.log("Invite accepted successfully");
				} else if (
					response.error?.code === "USER_ALREADY_IN_CONTEST" ||
					response.code === "USER_ALREADY_IN_CONTEST"
				) {
					// Handle user already in contest error
					const errorDetails =
						response.error?.detail || response.error?.details;
					console.log("User already in contest with:", errorDetails);

					const currentPartyName = errorDetails?.partyname || "another party";
					const contestName = contest || "this contest";
					const message = `You're already in the ${contestName} contest with the ${currentPartyName} party. You can only be in one party per contest.`;
					toast.error(message, { duration: 6000 });
					setError(message);
				} else {
					console.error("Failed to accept invite:", response.error);
					const errorMessage =
						response.error?.message ||
						response.message ||
						"Failed to accept invitation";
					toast.error(errorMessage);
					setError(errorMessage);
				}
			} catch (error) {
				console.error("Error processing invite acceptance:", error);

				// Check if the error response contains the party conflict information
				if (
					error.response?.data?.error?.code === "USER_ALREADY_IN_CONTEST" ||
					error.response?.data?.code === "USER_ALREADY_IN_CONTEST"
				) {
					const errorData = error.response.data;
					const errorDetails =
						errorData.error?.detail || errorData.error?.details;
					const currentPartyName = errorDetails?.partyname || "another party";
					const contestName = inviteParams?.contest || "this contest";
					const message = `You're already in the ${contestName} contest with the ${currentPartyName} party. You can only be in one party per contest.`;
					toast.error(message, { duration: 6000 });
					setError(message);
				} else {
					const errorMessage =
						error.message ||
						"An error occurred while processing the invitation";
					toast.error(errorMessage);
					setError(errorMessage);
				}
			} finally {
				setIsProcessing(false);
			}
		},
		[state.user, state.setUserWithRoles]
	);

	// Auto-process invite when component mounts and user is authenticated
	useEffect(() => {
		const inviteParams = getInviteParams();

		// Check for pending invite confirmation from AcceptInvite page
		const pendingConfirmation = localStorage.getItem(
			"pendingInviteConfirmation"
		);
		if (
			pendingConfirmation &&
			state.user?.MemberID &&
			state.getAccessLevel() > 0
		) {
			try {
				const modalData = JSON.parse(pendingConfirmation);
				console.log(
					"Found pending invite confirmation, showing modal:",
					modalData
				);
				setInviteData(modalData);
				setShowModal(true);
				// Clear the pending confirmation
				localStorage.removeItem("pendingInviteConfirmation");
				return;
			} catch (error) {
				console.error("Error parsing pending invite confirmation:", error);
				localStorage.removeItem("pendingInviteConfirmation");
			}
		}

		// Only process if we have invite parameters AND user is authenticated
		// AND we're actually in an invite flow (not just any page with some params)
		if (inviteParams && state.user?.MemberID && state.getAccessLevel() > 0) {
			// Check if this is actually an invite flow by looking for key invite indicators
			// BUT exclude judge invites - they have their own special flow
			const isJudgeInvite =
				inviteParams.role === "judge" &&
				inviteParams.contestId &&
				inviteParams.gameMasterID;
			const isInviteFlow =
				!isJudgeInvite &&
				(inviteParams.acceptingInvitation === true ||
					inviteParams.partyId ||
					inviteParams.contestId ||
					inviteParams.role ||
					inviteParams.inviteType ||
					inviteParams.gameMasterID);

			if (isJudgeInvite) {
				console.log(
					"Judge invite detected, skipping regular invite processing - judge has its own onboarding flow"
				);
			} else if (isInviteFlow) {
				console.log("Auto-processing invite acceptance for invite flow");
				processInviteAcceptance(inviteParams);
			} else {
				console.log("No invite flow detected, skipping invite processing");
			}
		}
	}, [
		state.user?.MemberID,
		state.getAccessLevel() > 0,
		getInviteParams,
		processInviteAcceptance,
	]);

	// Check if we're on an invite URL (but exclude judge invites)
	const inviteParams = getInviteParams();
	const isJudgeInvite =
		inviteParams?.role === "judge" &&
		inviteParams?.contestId &&
		inviteParams?.gameMasterID;
	const isInviteURL =
		!isJudgeInvite &&
		(location.pathname.startsWith("/accept-invite") ||
			location.pathname.startsWith("/QRvalidate") ||
			inviteParams !== null);

	return {
		inviteData,
		isProcessing,
		showModal,
		error,
		isInviteURL,
		processInviteAcceptance,
		setShowModal,
	};
};
